import { addDays, format, parseISO } from 'date-fns';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { TrendRow, TrendSeries } from '@Components/Charts/TrendLineChart/trend-line-chart-types';
import { getProductPositionStats } from './alexa-visibility-citation-utils';
import {
  findProductBrand,
  getAvgPositionWithRankedShare,
  getMentionedBrands,
  getResultProducts,
  getShareOfVoice,
  getVisibilityScore,
  groupBy,
  isSameBrand,
  normalizeBrand
} from './alexa-visibility-utils';

const DATE_KEY_FORMAT = 'yyyy-MM-dd';
const SLOTS_PER_RESPONSE = 3;
const WINDOW_DAYS = 30;

/** The local calendar day a result was scraped on, e.g. "2026-06-18". */
export const toDateKey = (createdAt: string): string =>
  format(parseISO(createdAt), DATE_KEY_FORMAT);

const rowsByDate = (
  results: AlexaVisibilityResult[],
  keys: string[],
  valueFor: (dayResults: AlexaVisibilityResult[], key: string) => number | null
): TrendRow[] =>
  Array.from(groupBy(results, result => toDateKey(result.createdAt)).entries())
    .map(([date, dayResults]) => ({
      date,
      values: Object.fromEntries(keys.map(key => [key, valueFor(dayResults, key)]))
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

/**
 * Per run date, the % of responses that mention each brand. The target brand uses the classifier's
 * own "brand mentioned" flag, exactly as the Visibility Score does elsewhere on the page, so its
 * line matches the other tabs; competitors only have the list of brands mentioned.
 */
export const getVisibilityByDate = (
  results: AlexaVisibilityResult[],
  brands: string[],
  targetBrand: string
): TrendRow[] =>
  rowsByDate(results, brands, (dayResults, brand) => {
    if (dayResults.length === 0) return null;
    if (isSameBrand(brand, targetBrand)) return getVisibilityScore(dayResults);
    return (
      (dayResults.filter(result =>
        result.classification?.brandsMentioned?.some(mentioned => isSameBrand(mentioned, brand))
      ).length /
        dayResults.length) *
      100
    );
  });

/**
 * The share of top-3 recommendation slots a brand holds in these results: its products' slots
 * divided by every slot that was actually filled. A product that matches none of the mentioned
 * brands fills a slot but belongs to nobody. `null` when no slot was filled.
 */
export const getSoarShare = (
  results: AlexaVisibilityResult[],
  brand: string,
  mentionedBrands = getMentionedBrands(results)
): number | null => {
  let filled = 0;
  let held = 0;
  results.forEach(result => {
    getResultProducts(result)
      .slice(0, SLOTS_PER_RESPONSE)
      .forEach(product => {
        filled += 1;
        const owner = findProductBrand(product, mentionedBrands);
        if (owner !== undefined && isSameBrand(owner, brand)) {
          held += 1;
        }
      });
  });
  return filled === 0 ? null : (held / filled) * 100;
};

/** Per run date, each brand's share of the top-3 recommendation slots. */
export const getSoarByDate = (results: AlexaVisibilityResult[], brands: string[]): TrendRow[] => {
  const mentioned = getMentionedBrands(results);
  return rowsByDate(results, brands, (dayResults, brand) =>
    getSoarShare(dayResults, brand, mentioned)
  );
};

export interface BrandTopProducts {
  brand: string;
  // Its most recommended products, most first.
  products: string[];
}

/** Each brand's `perBrand` most recommended products across the results. */
export const getTopProductsByBrand = (
  results: AlexaVisibilityResult[],
  brands: string[],
  perBrand = 3
): BrandTopProducts[] => {
  const products = getProductPositionStats(results);
  return brands.map(brand => ({
    brand,
    products: products
      .filter(product => product.brand !== undefined && isSameBrand(product.brand, brand))
      .slice(0, perBrand)
      .map(product => product.label)
  }));
};

/** Per run date, the % of responses that recommend each product in their top 3. */
export const getProductTrendByDate = (
  results: AlexaVisibilityResult[],
  products: string[]
): TrendRow[] =>
  rowsByDate(results, products, (dayResults, product) =>
    dayResults.length === 0
      ? null
      : (dayResults.filter(result =>
          getResultProducts(result).some(item => normalizeBrand(item) === normalizeBrand(product))
        ).length /
          dayResults.length) *
        100
  );

/** Date plus one column per series, for the Excel export of a brand trend chart. */
export const buildTrendExportRows = (
  rows: TrendRow[],
  series: TrendSeries[]
): Record<string, unknown>[] =>
  rows.map(row => ({
    Date: row.date,
    ...Object.fromEntries(
      series.map(({ key, label }) => [
        label,
        row.values[key] === null || row.values[key] === undefined
          ? ''
          : Number(row.values[key]?.toFixed(1))
      ])
    )
  }));

export interface TrendWindows {
  current: AlexaVisibilityResult[];
  previous: AlexaVisibilityResult[];
  // The last day of the current window (the newest run), e.g. "2026-10-09".
  endDate: string | undefined;
}

/**
 * The newest 30 days of results and the 30 days before them, counted back from the newest run so
 * both windows are always the same length, even mid-month.
 */
export const getTrendWindows = (results: AlexaVisibilityResult[]): TrendWindows => {
  const dates = results.map(result => toDateKey(result.createdAt)).sort();
  const endDate = dates[dates.length - 1];
  if (endDate === undefined) return { current: [], previous: [], endDate };
  const dayKey = (offset: number): string =>
    format(addDays(parseISO(endDate), offset), DATE_KEY_FORMAT);
  const currentStart = dayKey(-(WINDOW_DAYS - 1));
  const previousStart = dayKey(-(2 * WINDOW_DAYS - 1));
  return {
    endDate,
    current: results.filter(result => toDateKey(result.createdAt) >= currentStart),
    previous: results.filter(result => {
      const day = toDateKey(result.createdAt);
      return day >= previousStart && day < currentStart;
    })
  };
};

export interface MetricChange {
  current: number | null;
  previous: number | null;
  // current - previous, in the metric's own units (percentage points, or positions).
  delta: number | null;
  // Whether the change is good for the brand; undefined when it can't be compared or didn't move.
  improved: boolean | undefined;
}

const toChange = (
  current: number | null,
  previous: number | null,
  lowerIsBetter = false
): MetricChange => {
  const delta = current !== null && previous !== null ? current - previous : null;
  if (delta === null || delta === 0) {
    return { current, previous, delta, improved: undefined };
  }
  return { current, previous, delta, improved: lowerIsBetter ? delta < 0 : delta > 0 };
};

export interface MonthOverMonth {
  visibilityScore: MetricChange;
  soar: MetricChange;
  shareOfVoice: MetricChange;
  avgPosition: MetricChange;
}

/** The brand's headline metrics in the newest 30 days against the 30 days before. */
export const getMonthOverMonth = (windows: TrendWindows, targetBrand: string): MonthOverMonth => {
  const metric = (
    results: AlexaVisibilityResult[],
    fn: (rows: AlexaVisibilityResult[]) => number | null
  ): number | null => (results.length === 0 ? null : fn(results));
  const compare = (fn: (rows: AlexaVisibilityResult[]) => number | null, lowerIsBetter = false) =>
    toChange(metric(windows.current, fn), metric(windows.previous, fn), lowerIsBetter);
  return {
    visibilityScore: compare(getVisibilityScore),
    soar: compare(rows => getSoarShare(rows, targetBrand)),
    shareOfVoice: compare(rows => getShareOfVoice(rows, targetBrand)),
    avgPosition: compare(rows => getAvgPositionWithRankedShare(rows).avgPosition ?? null, true)
  };
};

export interface QueryChange {
  query: string;
  previous: number;
  current: number;
  // Percentage points.
  delta: number;
}

/**
 * The queries whose visibility score moved most between the two windows: `improvements` gained the
 * most, `declines` lost the most. A query must have results in both windows to be compared.
 */
export const getQueryChanges = (
  windows: TrendWindows,
  limit = 5
): { improvements: QueryChange[]; declines: QueryChange[] } => {
  const previousByQuery = groupBy(windows.previous, result => result.query);
  const currentByQuery = groupBy(windows.current, result => result.query);
  const changes: QueryChange[] = [];
  currentByQuery.forEach((currentResults, query) => {
    const previousResults = previousByQuery.get(query);
    if (!previousResults) return;
    const current = getVisibilityScore(currentResults);
    const previous = getVisibilityScore(previousResults);
    changes.push({ query, previous, current, delta: current - previous });
  });
  const byQuery = (a: QueryChange, b: QueryChange): number => a.query.localeCompare(b.query);
  return {
    improvements: changes
      .filter(change => change.delta > 0)
      .sort((a, b) => b.delta - a.delta || byQuery(a, b))
      .slice(0, limit),
    declines: changes
      .filter(change => change.delta < 0)
      .sort((a, b) => a.delta - b.delta || byQuery(a, b))
      .slice(0, limit)
  };
};

/** One row per product and run date, for the Excel export of the top-products trends. */
export const buildProductTrendExportRows = (
  results: AlexaVisibilityResult[],
  groups: BrandTopProducts[]
): Record<string, unknown>[] =>
  groups.flatMap(({ brand, products }) =>
    getProductTrendByDate(results, products).flatMap(row =>
      products.map(product => ({
        Date: row.date,
        Brand: brand,
        Product: product,
        '% of Responses': Number((row.values[product] ?? 0).toFixed(1))
      }))
    )
  );
